/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import {
  FormLabel,
  Link,
  Button,
  sizes,
  Spacer,
  TextInput,
  TypeRampV2,
} from '@smartsheet/lodestar-core';
import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { setToken, getRedirectAfterSignInPath } from '../../../../utils/signIn';
import { SignInStage } from '../../../pages/SignIn/SignIn';
import { SignInWithSmartsheet } from '../SignInWithSmartsheet';
declare const grecaptcha: any;

const inputContainerStyle = css`
  padding-top: ${sizes.large}px;
  padding-bottom: ${sizes.large}px;
`;

export const SignInEmailPassword = ({
  setStage,
}: {
  setStage: React.Dispatch<React.SetStateAction<SignInStage>>;
}) => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const { t: translate } = useTranslation();

  useEffect(() => {
    const script = document.createElement('script');

    script.src =
      'https://www.recaptcha.net/recaptcha/api.js?render=6LdoaeoUAAAAAMYo-W5tVKkMTZ99ajilS3ty2xK8';
    script.async = true;

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  function signin(email: string, password: string, captchaToken: string) {
    const config = {
      method: 'post',
      body: JSON.stringify({
        timeout: 36000,
        autoRenew: true,
        email: email,
        password: password,
        captchaToken: captchaToken,
      }),
    };
    return config;
  }

  const signIn = () => {
    setError('');
    grecaptcha.ready(async () => {
      const captchaToken = await grecaptcha.execute(
        '6LdoaeoUAAAAAMYo-W5tVKkMTZ99ajilS3ty2xK8',
        { action: 'login' }
      );
      setIsLoading(true);
      const config = signin(email, password, captchaToken);
      const authResponse = await fetch(
        '/api/user/verify?createToken=true',
        config
      );
      if (!authResponse.ok) {
        if (authResponse.status === 401) {
          setError(translate('signin:validationMessage'));
        }
      } else {
        const parsedAuthResponse = await authResponse.json();
        setToken(JSON.stringify(parsedAuthResponse.token));
        window.location.href = getRedirectAfterSignInPath();
      }
      setIsLoading(false);
    });
  };

  const backHandler = () => {
    setStage(SignInStage.SELECTION);
  };

  return (
    <React.Fragment>
      <TypeRampV2 system="lodestar" variant="headlineMedium">
        {translate('signin:signinToBridge')}
      </TypeRampV2>
      <form
        id="emailSignIn"
        css={inputContainerStyle}
        onSubmit={e => e.preventDefault()}
      >
        <FormLabel label={translate('signin:email')}>
          {inputProps => (
            <TextInput
              value={email}
              onChange={setEmail}
              validationState={error.length ? 'error' : undefined}
              aria-required="true"
              aria-invalid={error.length > 0}
              {...inputProps}
            />
          )}
        </FormLabel>
        <FormLabel
          label={translate('signin:password')}
          validationMessage={error}
          validationState={error.length ? 'error' : undefined}
        >
          {inputProps => (
            <TextInput
              value={password}
              type="password"
              onChange={setPassword}
              aria-required="true"
              aria-invalid={error.length > 0}
              validationState={error.length ? 'error' : undefined}
              {...inputProps}
            />
          )}
        </FormLabel>
      </form>
      <Spacer
        orientation="row"
        space="small"
        css={css`
          padding-bottom: ${sizes.medium}px;
        `}
      >
        <Button
          appearance="secondary"
          shouldFitContainer={true}
          onClick={backHandler}
        >
          {translate('common:back')}
        </Button>
        <Button
          appearance="primary"
          onClick={signIn}
          isLoading={isLoading}
          shouldFitContainer={true}
          type="submit"
          form="emailSignIn"
        >
          {translate('signin:signin')}
        </Button>
      </Spacer>
      <Link href="/#/resetpassword">{translate('signin:resetPassword')}</Link>
      <div
        css={css`
          padding-top: ${sizes.small}px;
        `}
      >
        <TypeRampV2 system="lodestar" variant="bodyText" renderAs="p">
          <Trans i18nKey="signin:termsOfUse">
            By continuing, you agree to the
            <Link
              href="http://www.smartsheet.com/legal/user-agreement"
              target="_blank"
            >
              Terms of Use.
            </Link>
            Our
            <Link
              href="http://www.smartsheet.com/legal/privacy"
              target="_blank"
            >
              Privacy Policy
            </Link>{' '}
            describes how we process personal data.
          </Trans>
        </TypeRampV2>
      </div>
      <SignInWithSmartsheet />
    </React.Fragment>
  );
};
